import { useRef, useState } from 'react'

import Loader from '@space-core/components/loader'
import useTranslation from '@space-core/hooks/useTranslation'
import { customMedia } from '@space-core/styles'

import Section from '@space-shell/common/components/section'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import useRequest from '@space-shell/common/hooks/useRequest'
import { BundleEndpoints, BundleService } from '@space-shell/common/services/bundle'
import { ProductUtils } from '@space-shell/common/utils/product'

import { useViewAsStore } from '@space-shell/core/store'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import BundleSection from './bundleSection'

import { DEFAULT_CURRENT_PAGE, DEFAULT_PAGE_SIZE } from './constants'

import { BundleProvider } from './context'
import ErrorState from './errorState'
import useIntersectionObserver from './hooks/useIntersectionObserver'

export type BundleProps = {
  pageSize?: number
  currentPage?: number
}

const SectionWrapper = styled(Section)`
  margin-bottom: calc(var(--spacer-12) + var(--spacer-4));

  ${customMedia.lessThan('md')`
    margin-bottom: var(--spacer-12);
  `}
`

const Bundle = ({ pageSize = 0, currentPage = 0 }: BundleProps) => {
  const [enableBundleRequest, setEnableBundleRequest] = useState<boolean>(false)
  const { authStatus } = useAuthContext()
  const isUserAuthenticated = Boolean(authStatus === AuthStatus.AUTHENTICATED)
  const { isEnabled: isViewAsEnabled } = useViewAsStore()

  const { membership } = useParams()
  const membershipSlug = membership as string
  const { t } = useTranslation()
  const observer = useRef<IntersectionObserver>()

  const elementRef = useIntersectionObserver({
    observer,
    fetch: () => setEnableBundleRequest((enableBundleRequest) => !enableBundleRequest)
  })

  const {
    isLoading,
    isError,
    invalidate,
    data: bundles
  } = useRequest(
    [BundleEndpoints.bundles(membershipSlug), String(isUserAuthenticated), String(isViewAsEnabled)],
    () =>
      BundleService.getBundles(membershipSlug, {
        size: pageSize ? pageSize : DEFAULT_PAGE_SIZE,
        page: currentPage ? currentPage : DEFAULT_CURRENT_PAGE
      }),
    { enabled: enableBundleRequest }
  )

  if (isLoading)
    return (
      <div ref={elementRef}>
        <Loader full />
      </div>
    )

  if (isError)
    return (
      <SectionWrapper title={t('sections.bundle.title')} className='_px-4 _px-md-10 _mb-12'>
        <ErrorState onTryAgain={invalidate} />
      </SectionWrapper>
    )

  if (!bundles?.content?.length) return null

  const parsedBundle = ProductUtils.convertToBundleCard(bundles.content)

  return (
    <SectionWrapper title={t('sections.bundle.title')} className='_px-4 _px-md-10 _mb-12'>
      <BundleProvider>{bundles && <BundleSection bundles={parsedBundle} />}</BundleProvider>
    </SectionWrapper>
  )
}

export default Bundle
